/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useMemo, useState } from 'react'

import { useApi } from '@/hooks'

import { STATUS_LABEL } from './SideBySide'
import type { MigrationFilters, MigrationRun, RowStatus, SqlPair } from './types'
import { wallClock } from './useMigrationFilters'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from '../adminTable'

const pad = (n: number) => String(n).padStart(2, '0')

/** When a run happened, in the viewer's local time. */
export const formatRanAt = (v: string | Date): string => {
  const d = v instanceof Date ? v : new Date(v)
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`
}

/** A recorded verdict, compact: match/mismatch/error with Fresh/Timescale counts. */
export const RunVerdict: React.FC<{ run: MigrationRun }> = ({ run }) => {
  if (run.match === null) {
    return (
      <span className="badge badge-error badge-sm" title={run.freshError ?? run.timescaleError ?? ''}>
        error
      </span>
    )
  }
  return (
    <span className={`badge badge-sm ${run.match ? 'badge-success' : 'badge-error'}`}>
      {run.match ? 'match' : 'mismatch'} · {run.freshCount}/{run.timescaleCount}
    </span>
  )
}

/** The dashboard's "Last run" cell. */
export const LastRun: React.FC<{ run: MigrationRun | null }> = ({ run }) =>
  run ? (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <RunVerdict run={run} />
      <span className="text-xs text-base-content/60" title={run.ranBy ?? ''}>
        {formatRanAt(run.ranAt)}
      </span>
    </div>
  ) : (
    <span className="text-base-content/40">never</span>
  )

/** The run's non-matching row counts, e.g. "Missing in Timescale 3"; the error when a side failed. */
const discrepancies = (run: MigrationRun): string =>
  run.match === null
    ? (run.freshError ?? run.timescaleError ?? '')
    : (Object.entries(run.summary.counts ?? {}) as [RowStatus, number][])
        .filter(([status, n]) => status !== 'match' && n > 0)
        .map(([status, n]) => `${STATUS_LABEL[status]} ${String(n)}`)
        .join(', ')

const discrepancyCount = (run: MigrationRun): number =>
  (Object.entries(run.summary.counts ?? {}) as [RowStatus, number][])
    .filter(([status]) => status !== 'match')
    .reduce((sum, [, n]) => sum + n, 0)

interface Props {
  itemId: string
  sql: SqlPair[]
  /** Bump to reload, e.g. after a run on this page. */
  refreshKey: number
  onUseFilters: (filters: Partial<MigrationFilters>) => void
}

/** Every recorded run of an item, newest first, with the filters it ran with. */
export const RunHistory: React.FC<Props> = ({ itemId, sql, refreshKey, onUseFilters }) => {
  const { api } = useApi()
  const [runs, setRuns] = useState<MigrationRun[]>([])
  const [error, setError] = useState('')

  useEffect(() => {
    const load = async () => {
      const res = await api.get<MigrationRun[]>({
        path: `/app/admin/migration/items/${encodeURIComponent(itemId)}/runs?take=100`,
        origin: 'MigrationItemPage#runs',
      })
      if (res.error || !res.data) {
        setError(res.message || 'Could not load the run history')
        return
      }
      setError('')
      setRuns(res.data)
    }
    void load()
  }, [api, itemId, refreshKey])

  const columns = useMemo<AdminColumns<MigrationRun>>(() => {
    const what = (target: string) =>
      target === 'test' ? 'API test' : (sql.find((p) => `sql:${p.id}` === target)?.title ?? target)
    return [
      {
        id: 'ranAt',
        header: 'Ran',
        accessorFn: (run) => new Date(run.ranAt).getTime(),
        meta: { className: 'whitespace-nowrap' },
        cell: ({ row }) => formatRanAt(row.original.ranAt),
      },
      { id: 'what', header: 'What', accessorFn: (run) => what(run.target) },
      {
        id: 'result',
        header: 'Result',
        // Errors, then mismatches, then matches.
        accessorFn: (run) => (run.match === null ? 0 : run.match ? 2 : 1),
        cell: ({ row }) => <RunVerdict run={row.original} />,
      },
      {
        id: 'discrepancies',
        header: 'Discrepancies',
        accessorFn: discrepancyCount,
        meta: { className: 'text-xs' },
        cell: ({ row }) => discrepancies(row.original),
      },
      { id: 'company', header: 'Company', accessorFn: (run) => run.companyId },
      {
        id: 'asset',
        header: 'Asset',
        accessorFn: (run) => run.assetId ?? undefined,
        cell: ({ row }) => row.original.assetId ?? '—',
      },
      {
        id: 'range',
        header: 'Range',
        accessorFn: (run) => new Date(run.from).getTime(),
        meta: { className: 'whitespace-nowrap text-xs' },
        cell: ({ row }) => (
          <>
            {wallClock(row.original.from)?.replace('T', ' ')} → {wallClock(row.original.to)?.replace('T', ' ')}
          </>
        ),
      },
      {
        id: 'decimals',
        header: 'Decimals',
        accessorFn: (run) => run.coordDigits ?? undefined,
        meta: { className: 'text-xs' },
        cell: ({ row }) => (
          <>
            {row.original.coordDigits ?? '—'} / {row.original.numberDigits ?? '—'}
          </>
        ),
      },
      {
        id: 'ranBy',
        header: 'By',
        accessorFn: (run) => run.ranBy ?? undefined,
        meta: { className: 'text-xs' },
      },
      {
        id: 'useFilters',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const run = row.original
          return (
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              title="Set the filters to this run's"
              onClick={() =>
                onUseFilters({
                  companyId: run.companyId,
                  assetId: run.assetId ?? undefined,
                  from: wallClock(run.from),
                  to: wallClock(run.to),
                  ...(run.coordDigits !== null ? { coordDigits: run.coordDigits } : {}),
                  ...(run.numberDigits !== null ? { numberDigits: run.numberDigits } : {}),
                })
              }
            >
              Use filters
            </button>
          )
        },
      },
    ]
  }, [sql, onUseFilters])

  const table = useAdminTable(runs, columns, { getRowId: (run) => String(run.id) })

  if (error) return <p className="text-sm text-error">{error}</p>
  if (!runs.length) return <p className="text-sm text-base-content/60">No runs recorded yet.</p>

  return (
    <div className="overflow-x-auto">
      <table className="table table-xs" data-testid="migration-run-history">
        <thead>
          <tr>
            <AdminHeaderCells table={table} />
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              <AdminRowCells row={row} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
